/**
 * Block-related enums.
 *
 * All enums match database enum types exactly.
 * These are the source of truth for discriminated unions.
 */

// ========== Block Type Enum ==========

/**
 * All block types in the system.
 * This is the discriminator for the block union type.
 */
export type PlayBlockType =
	// ========== Canvas Container Blocks (root level) ==========
	| 'play' // A complete play diagram
	| 'formation' // Template for player positions
	| 'concept' // Formation + route templates
	// ========== Canvas Content Blocks (nested) ==========
	| 'player' // Individual player on canvas
	| 'drawing' // Route/path container
	| 'control_point' // Bezier point within drawing (handles embedded in properties.handles)
	| 'annotation' // Text label on canvas
	| 'pre_snap_marker' // Motion/shift indicator
	| 'concept_reference' // Link to concept (for plays)
	// ========== Document Container Blocks (root level) ==========
	| 'document' // Printable document (call sheet, wristband, etc.)
	// ========== Document Content Blocks (nested) ==========
	| 'section' // Grouping within call sheet
	| 'text' // Text content
	| 'header' // Section header
	| 'divider' // Visual separator
	| 'image' // Image in documents or slides
	| 'shape' // Geometric shape on canvas or slides
	// ========== Layout Container Blocks ==========
	| 'row' // Horizontal layout container
	| 'column' // Vertical layout container
	// ========== Reference Blocks (leaf) ==========
	| 'play_reference' // Embed a play
	| 'formation_reference' // Embed a formation
	| 'document_reference' // Embed a document in presentation

// ========== Canvas Enums ==========

/** Hash mark position on the field */
export type HashPosition = 'left' | 'middle' | 'right'

/** How concepts target players */
export type TargetingMode = 'absolute_role' | 'relative_selector'

/** Ball position for concept targeting */
export type BallPosition = 'left' | 'center' | 'right'

/** Play direction for concept targeting */
export type PlayDirection = 'left' | 'right' | 'na'

/** Type of control point in a drawing */
export type ControlPointType = 'start' | 'end' | 'corner' | 'intermediate' | 'control' | 'snap'

/**
 * @deprecated BezierHandleBlock was removed. Handles are now embedded in
 * ControlPointBlock.properties.handles. This type is retained only for
 * schema compatibility in BlockProperties.ts.
 */
export type BezierHandleType = 'in' | 'out'

/**
 * @deprecated PathSegmentBlock was removed. Path segments are now represented
 * as block_references with reference_type='path_connection'. This type is
 * retained only for schema compatibility in BlockProperties.ts.
 */
export type PathSegmentType = 'line' | 'quadratic' | 'cubic'

/** Line style for drawings */
export type LineStyle = 'solid' | 'dashed'

/** Line ending style for drawings */
export type LineEnd = 'none' | 'arrow' | 'tShape'

/** Path mode for drawings (sharp corners vs curves) */
export type PathMode = 'sharp' | 'curve'

/** Pre-snap motion type */
export type PreSnapMotionType = 'motion' | 'shift'

// ========== Document Enums ==========

/** Document subtype */
export type DocumentType = 'call_sheet' | 'wristband' | 'practice_script'

/** Text alignment */
export type TextAlignment = 'left' | 'center' | 'right'

/** Divider line style */
export type DividerStyle = 'solid' | 'dashed' | 'dotted'

// ========== Reference Type Enum ==========

/**
 * Block reference types for the block_references junction table.
 */
export type ReferenceType =
	| 'formation_base' // Play/Concept uses Formation as base template
	| 'formation_linked' // Bidirectional linked formation pair
	| 'concept_include' // Play includes a Concept
	| 'content_embed' // Slide/Section embeds Play/Formation/Document
	| 'player_template' // Player derived from another Player
	| 'player_link' // Drawing linked to a Player
	| 'point_link' // Drawing start point linked to ControlPoint
	| 'attachment' // Annotation attached to Player/Drawing
	| 'path_connection' // Connection between two control points in a drawing

// ========== Block Type Sets ==========

/**
 * Block types that can exist at root level (no parent).
 */
export const ROOT_BLOCK_TYPES: ReadonlyArray<PlayBlockType> = [
	'play',
	'formation',
	'concept',
	'document',
] as const

/**
 * Canvas root block types (spatial positioning).
 */
export const CANVAS_ROOT_TYPES: ReadonlyArray<PlayBlockType> = [
	'play',
	'formation',
	'concept',
] as const

/**
 * Document root block types.
 */
export const DOCUMENT_ROOT_TYPES: ReadonlyArray<PlayBlockType> = [
	'document',
] as const

/**
 * Canvas content block types (children of plays/formations/concepts).
 */
export const CANVAS_CONTENT_TYPES: ReadonlyArray<PlayBlockType> = [
	'player',
	'drawing',
	'control_point',
	'annotation',
	'pre_snap_marker',
] as const

/**
 * Document content block types (children of documents/presentations).
 */
export const DOCUMENT_CONTENT_TYPES: ReadonlyArray<PlayBlockType> = [
	'section',
	'text',
	'header',
	'divider',
	'shape',
	'image',
] as const

/**
 * Block types that are layout containers.
 */
export const LAYOUT_CONTAINER_TYPES: ReadonlyArray<PlayBlockType> = [
	'play',
	'formation',
	'concept',
	'document',
	'section',
	'row',
	'column',
] as const

/**
 * Reference block types (embed or link to other blocks).
 */
export const REFERENCE_BLOCK_TYPES: ReadonlyArray<PlayBlockType> = [
	'concept_reference',
	'play_reference',
	'formation_reference',
	'document_reference',
] as const

// ========== Type Guards for Block Type Sets ==========

export function isRootBlockType(type: PlayBlockType): boolean {
	return ROOT_BLOCK_TYPES.includes(type)
}

export function isCanvasRootType(type: PlayBlockType): boolean {
	return CANVAS_ROOT_TYPES.includes(type)
}

export function isDocumentRootType(type: PlayBlockType): boolean {
	return DOCUMENT_ROOT_TYPES.includes(type)
}

export function isCanvasContentType(type: PlayBlockType): boolean {
	return CANVAS_CONTENT_TYPES.includes(type)
}

export function isDocumentContentType(type: PlayBlockType): boolean {
	return DOCUMENT_CONTENT_TYPES.includes(type)
}

export function isLayoutContainerType(type: PlayBlockType): boolean {
	return LAYOUT_CONTAINER_TYPES.includes(type)
}

export function isReferenceBlockType(type: PlayBlockType): boolean {
	return REFERENCE_BLOCK_TYPES.includes(type)
}
